"use client";
import { DelegatedPermissionRequestContent } from "@/features/reserve/components/Reserve/DelegatedPermissionRequest";
import { useReserveForm } from "@/features/reserve/components/Reserve/ReserveFormProvider";
import { ReserveOptions } from "@/features/reserve/components/Reserve/types";
import { IResource, ResourceType } from "@gettactic/api";
import { UseDisclosureReturn } from "@gettactic/helpers";
import { useResourceEvents } from "@gettactic/hooks";
import { isSameDay } from "date-fns";
import { Card } from "@cmp-src/data-display/Card/Card";
import {
  MobileMapBottomMargin,
  SurfaceCardClasses,
  SurfaceCardShellMarginMd
} from "@cmp-src/data-display/Card/classes";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import React, { useCallback } from "react";
import { MapReserveCardInner } from "./MapReserveCardInner";
import { useMap } from "@/features/map/MapProvider";

export interface MapReserveCardProps
  extends Omit<ReserveOptions, "resource" | "resourceType"> {
  resourceType?: ResourceType;
  resource?: IResource | null;
  onCancel?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  dayViewDisclosure?: UseDisclosureReturn;
  requireDelegatedPermission?: boolean;
  msAuthUrl?: string;
  isCompact?: boolean;
  isModal?: boolean;
  onSuccess?: () => void;
  isOnReservationModal?: boolean;
}
const classes = {
  Card: `${SurfaceCardClasses} ${MobileMapBottomMargin} absolute inset-0 z-sticky p-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2/3 data-[state=open]:fade-in-0 data-[state=open]:animate-in data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out`,
  CardContent: "p-0 overflow-hidden h-full flex flex-col",
  CoverWrapper: "relative h-60 w-full overflow-hidden",
  CoverImage:
    "flex h-full w-full items-center justify-center text-center bg-neutral-200",
  CoverControlsWrapper: "absolute inset-0 top-5 md:pt-0 p-5",
  CoverControlsContainer: "flex justify-between",
  BackButton: "rounded-full bg-black/50",
  BackIcon: "size-4 text-white",
  FavoriteButton: "bg-white",
  FavoriteIcon: "size-4 text-neutral-600",
  ScrollArea: "flex flex-1",
  ScrollAreaContent: "flex flex-col gap-6 p-3 pt-6",

  // card should fade gradient to top
  ActionButtonsWrapper:
    "p-3 flex flex-col gap-2.5 from-white/100 via-white/80 bg-gradient-to-t from-white to-transparent"
};

export const MapReserveCardContent: React.FC<
  MapReserveCardProps & {
    resource: IResource;
    haveAvailableSlots: boolean;
    onClose: () => void;
    isMd: boolean;
    isFull: boolean;
    isOpen: boolean;
    msAuthUrl?: string;
    requireDelegatedPermission?: boolean;
    isVisible: boolean;
  }
> = ({
  resource,
  haveAvailableSlots,
  onClose,
  isMd,
  isFull,
  isOpen,
  msAuthUrl,
  requireDelegatedPermission,
  isVisible,
  ...props
}) => {
  const { startTime, currentOffice } = useReserveForm();

  const { startDate: mapStartDate, changeStartDate } = useMap();

  // Track the last synced timestamp to avoid circular updates
  const lastSyncedTime = React.useRef<number | null>(null);

  React.useEffect(() => {
    // Only sync when the card is actually visible
    if (!startTime || !mapStartDate || !isVisible) return;

    const startTimeTs = startTime.getTime();

    // Avoid circular updates when time hasn't actually changed
    if (lastSyncedTime.current === startTimeTs) return;
    lastSyncedTime.current = startTimeTs;

    // On the full /map page we keep date+time in sync
    if (!props.isOnReservationModal) {
      changeStartDate(startTime);
      return;
    }

    // In the reservation modal, only propagate DATE changes to the map
    // so that MapFiltersCard time controls are not overwritten.
    const isSameCalendarDay = isSameDay(startTime, mapStartDate);

    if (!isSameCalendarDay) {
      const updatedDate = new Date(mapStartDate);
      updatedDate.setFullYear(
        startTime.getFullYear(),
        startTime.getMonth(),
        startTime.getDate()
      );

      changeStartDate(updatedDate);
    }
  }, [
    startTime,
    mapStartDate,
    changeStartDate,
    isVisible,
    props.isOnReservationModal
  ]);

  const { data: resourceSchedules } = useResourceEvents({
    time: startTime?.toISOString(),
    officeId: currentOffice?.id ?? null,
    tz: currentOffice?.time_zone,
    resourceId: resource?.id ?? null
  });

  const prevIsOpen = React.useRef(isOpen);
  const hasAutoOpened = React.useRef(false);
  const prevResourceId = React.useRef(resource?.id);

  // Auto-open/close daily view
  React.useEffect(() => {
    if (!props.dayViewDisclosure) return;

    // Handle closing transition
    if (prevIsOpen.current && !isOpen) {
      if (props.dayViewDisclosure.isOpen) {
        props.dayViewDisclosure.onOpenChange(false);
      }
    }
    prevIsOpen.current = isOpen;

    if (!isOpen) return;

    const hasSchedules = (resourceSchedules?.length ?? 0) > 0;
    const isMeetingRoom = resource?.type === "meeting_room";
    const resourceChanged = prevResourceId.current !== resource?.id;

    // Reset auto-open flag when resource changes
    if (resourceChanged) {
      hasAutoOpened.current = false;
      prevResourceId.current = resource?.id;
    }

    // Only auto-open if we haven't auto-opened for this resource yet
    // This allows users to manually close the day view and keep it closed
    if (
      (hasSchedules || isMeetingRoom) &&
      !props.dayViewDisclosure.isOpen &&
      !hasAutoOpened.current
    ) {
      props.dayViewDisclosure.onOpenChange(true);
      hasAutoOpened.current = true;
    }
  }, [
    isOpen,
    resource?.id,
    resource?.type,
    resourceSchedules?.length,
    props.dayViewDisclosure
  ]);

  const handleConnectOutlook = useCallback(() => {
    window.open(msAuthUrl);
  }, [msAuthUrl]);

  if (!isVisible || !resource?.id) return null;

  const content =
    !isFull && requireDelegatedPermission ? (
      <DelegatedPermissionRequestContent
        msAuthUrl={msAuthUrl}
        handleConnectOutlook={handleConnectOutlook}
      />
    ) : (
      <MapReserveCardInner
        haveAvailableSlots={haveAvailableSlots}
        {...props}
        msAuthUrl={msAuthUrl}
        requireDelegatedPermission={requireDelegatedPermission}
        onCancel={onClose}
        resource={resource}
        resourceSchedules={resourceSchedules ?? []}
      />
    );

  return (
    <>
      {isFull ? (
        <Card
          isFlat
          className={`v2-reservation ${classes.Card} ${props.isModal ? "md:m-0 md:border-card-border" : SurfaceCardShellMarginMd}`}
          contentProps={{ className: classes.CardContent }}
          hasShadow={false}
          data-state={isOpen ? "open" : "closed"}
        >
          {content}
        </Card>
      ) : (
        <MobileDrawer
          isOpen={isOpen}
          size={"full"}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              onClose?.();
            }
          }}
          contentUnderGrabber
          scrollableContent
          className="v2-reservation"
        >
          {content}
        </MobileDrawer>
      )}
    </>
  );
};
